import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { EnvelopeSimple, ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Backend responds with the same generic message whether or not the email exists,
      // so we always land in the "check your inbox" success state — this prevents user enumeration.
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100 p-6">
      <div className="w-full max-w-md bg-white border-2 border-zinc-900 p-8" data-testid="forgot-password-page">
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-orange-600"
            data-testid="back-to-login"
          >
            <ArrowLeft size={12} weight="bold" /> Back to login
          </Link>
        </div>

        {sent ? (
          <div data-testid="forgot-sent-state">
            <div className="w-12 h-12 bg-orange-600 text-white flex items-center justify-center mb-5">
              <PaperPlaneTilt size={24} weight="fill" />
            </div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight leading-tight mb-3">
              Check your inbox
            </h1>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              If an admin account matches <span className="font-mono font-bold">{email}</span>,
              we've sent a password reset link. The link expires in <strong>15 minutes</strong>.
            </p>
            <p className="text-xs text-zinc-500 leading-relaxed mb-6">
              Only admin accounts can reset via email. If you're not an admin, please ask an
              admin to unlock your account from Settings → Users.
            </p>
            <Link
              to="/login"
              className="kr-btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
            >
              Return to login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <div className="w-12 h-12 bg-orange-600 text-white flex items-center justify-center mb-5">
              <EnvelopeSimple size={24} weight="fill" />
            </div>
            <h1 className="font-display text-2xl font-black uppercase tracking-tight leading-tight mb-2">
              Reset your password
            </h1>
            <p className="text-sm text-zinc-500 mb-6 leading-relaxed">
              Enter your <strong>admin</strong> email. If it matches, we'll send a one-time reset link that's valid for 15 minutes.
            </p>

            <label className="block kr-label mb-1.5">Admin Email</label>
            <input
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              autoFocus
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
            />

            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="kr-btn-primary w-full mt-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="forgot-submit"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {busy ? "Sending…" : "Send reset link"}
            </button>

            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-6 text-center">
              Admin accounts only · 15-min expiry
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
